import { useEffect, useState } from "react";
import { Label, Form, FormGroup } from "reactstrap";
import { useHistory, useParams } from "react-router-dom";
import { useFormik } from "formik";
import toast from "react-hot-toast";

import { HeadingWithBackArrow } from "../../../components/HeadingWithBackArrow";
import { LoaderFallbackUI } from "../../../components/LoaderFallbackUI";
import { TextInput } from "../../../components/Input";
import { CustomButton } from "../../../components/CustomButton";

import typography from "../typography";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { ConfirmationModal } from "../../../components/CustomModal";
import ModalSuccessImage from "../../../assets/images/app/common/aou-modal-success-img.svg";
import { getAudioVideoContentById, updateAudioVideoContent } from "../../../api/api.service";
import { initializeMediaUtil, uploadOnS3, finalizeMediaUtil } from "../../../utils/mediaUtils";
import { validationSchema } from "../validation";
import {
    ACTIVITY_SLOT_OPTIONS,
    ALLOWED_IMAGE_EXTENSIONS,
    ALLOWED_MEDIA_EXTENSIONS,
    CONTENT_TYPE_OPTIONS,
    RESET_CONTEXT_OPTIONS,
    SLOT_ELIGIBLE_STATE_KEYS,
    STATE_KEY_OPTIONS,
} from "../data";
import FormikErrorText from "../../../components/FormikErrorText/FormikErrorText";
import WizardStepper from "../AddContent/WizardStepper";
import PreviewCard from "../AddContent/PreviewCard";
import FileDropzone from "../AddContent/FileDropzone";

// Same presentation-only grouping as AddContent.js — see that file's own
// comment. Kept as a separate copy (not a shared import) because
// AddContent/EditContent are, and always have been, independent components
// with their own field sets around this same core.
const WIZARD_STEPS = [
    { key: "basic", label: "Basic Details", fields: ["title", "author", "description"] },
    { key: "category", label: "Category & Type", fields: ["contentType", "stateKey", "activitySlot", "contexts"] },
    { key: "additional", label: "Additional Info", fields: ["goal", "teacherInstructions"] },
    { key: "upload", label: "Upload & Review", fields: [] },
];

export default function EditContent() {
    const { id } = useParams();
    const history = useHistory();

    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [existingContent, setExistingContent] = useState(null);

    // Replacing the file is optional on edit — null here means "keep the
    // existing one", distinct from AddContent.js where a file is mandatory.
    const [file, setFile] = useState(null);
    const [fileError, setFileError] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0);

    // Same "null = keep existing" convention as `file` above, plus an
    // explicit remove flag — this video has a third state the main video
    // doesn't: it can be cleared entirely, not just replaced.
    const [teacherGuideFile, setTeacherGuideFile] = useState(null);
    const [teacherGuideFileError, setTeacherGuideFileError] = useState("");
    const [teacherGuideUploadProgress, setTeacherGuideUploadProgress] = useState(0);
    const [removeTeacherGuide, setRemoveTeacherGuide] = useState(false);

    // Same three-state "keep / replace / remove" convention as teacherGuideFile
    // above — a thumbnail can be admin-set, auto-generated, or absent, and this
    // screen must be able to leave any of those alone, replace it, or clear it.
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailFileError, setThumbnailFileError] = useState("");
    const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState(0);
    const [removeThumbnail, setRemoveThumbnail] = useState(false);

    const [isSaving, setIsSaving] = useState(false);
    const [isModalActive, setIsModalActive] = useState(false);

    const [currentStep, setCurrentStep] = useState(0);
    const [maxStepReached, setMaxStepReached] = useState(0);
    const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState(null);

    const backButtonHandler = () => {
        history.push(APP_ROUTES.CONTENT_LIBRARY);
    };

    const formik = useFormik({
        initialValues: {
            title: "",
            author: "",
            description: "",
            goal: "",
            teacherInstructions: "",
            contentType: "VIDEO",
            stateKey: STATE_KEY_OPTIONS[0].value,
            activitySlot: "",
            contexts: [],
        },
        validationSchema,
        onSubmit: async (values) => {
            await handleSaveClick(values);
        },
    });

    const isSlotEligible = SLOT_ELIGIBLE_STATE_KEYS.includes(formik.values.stateKey);

    const isStepValid = (stepIndex) => !WIZARD_STEPS[stepIndex].fields.some((field) => Boolean(formik.errors[field]));

    const goNext = () => {
        if (!isStepValid(currentStep)) {
            const touchedPatch = {};
            WIZARD_STEPS[currentStep].fields.forEach((field) => {
                touchedPatch[field] = true;
            });
            formik.setTouched({ ...formik.touched, ...touchedPatch });
            return;
        }
        const next = Math.min(currentStep + 1, WIZARD_STEPS.length - 1);
        setCurrentStep(next);
        setMaxStepReached((prev) => Math.max(prev, next));
    };

    const goBack = () => setCurrentStep((step) => Math.max(step - 1, 0));

    const goToStep = (index) => {
        if (index <= maxStepReached) setCurrentStep(index);
    };

    const handleContextToggle = (value) => {
        const current = formik.values.contexts;
        const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
        formik.setFieldValue("contexts", next);
    };

    useEffect(() => {
        const fetchContent = async () => {
            if (!id) {
                setLoadError("Invalid content ID");
                return;
            }

            try {
                setLoading(true);
                setLoadError(null);
                const response = await getAudioVideoContentById(id);

                if (response) {
                    setExistingContent(response);
                    // setValues's default shouldValidate (true, following
                    // validateOnChange) already runs validation against
                    // these exact values and updates formik.errors from it —
                    // no separate validateForm() call needed (and a second
                    // one here raced the first, intermittently validating a
                    // stale/blank values snapshot and disabling Next on
                    // perfectly valid fetched data).
                    formik.setValues({
                        title: response.title || "",
                        author: response.author || "",
                        description: response.description || "",
                        goal: response.goal || "",
                        teacherInstructions: response.teacherInstructions || "",
                        contentType: response.contentType || "VIDEO",
                        stateKey: response.stateKey || STATE_KEY_OPTIONS[0].value,
                        activitySlot: response.activitySlot ?? "",
                        contexts: response.contexts ?? [],
                    });
                } else {
                    setLoadError("Content not found");
                }
            } catch (error) {
                console.error("Error fetching content:", error);
                setLoadError("Failed to load content details");
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Cosmetic only — lets the preview card show the actual chosen image
    // instead of the existing thumbnail/placeholder once a replacement is picked.
    useEffect(() => {
        if (!thumbnailFile) {
            setThumbnailPreviewUrl(null);
            return undefined;
        }
        const url = URL.createObjectURL(thumbnailFile);
        setThumbnailPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [thumbnailFile]);

    const handleFileChange = (e) => {
        const selected = e.target.files?.[0];
        if (!selected) return;

        const extension = selected.name.split(".").pop()?.toLowerCase();
        if (!ALLOWED_MEDIA_EXTENSIONS.includes(extension)) {
            setFileError(`Unsupported file type. Allowed: ${ALLOWED_MEDIA_EXTENSIONS.join(", ")}`);
            setFile(null);
            return;
        }

        setFileError("");
        setFile(selected);
    };

    const handleTeacherGuideFileChange = (e) => {
        const selected = e.target.files?.[0];
        if (!selected) return;

        const extension = selected.name.split(".").pop()?.toLowerCase();
        if (!ALLOWED_MEDIA_EXTENSIONS.includes(extension)) {
            setTeacherGuideFileError(`Unsupported file type. Allowed: ${ALLOWED_MEDIA_EXTENSIONS.join(", ")}`);
            setTeacherGuideFile(null);
            return;
        }

        setTeacherGuideFileError("");
        setTeacherGuideFile(selected);
        // Choosing a replacement file supersedes a pending removal.
        setRemoveTeacherGuide(false);
    };

    const handleRemoveTeacherGuide = () => {
        setTeacherGuideFile(null);
        setTeacherGuideFileError("");
        setRemoveTeacherGuide(true);
    };

    const handleUndoRemoveTeacherGuide = () => {
        setRemoveTeacherGuide(false);
    };

    const handleThumbnailFileChange = (e) => {
        const selected = e.target.files?.[0];
        if (!selected) return;

        const extension = selected.name.split(".").pop()?.toLowerCase();
        if (!ALLOWED_IMAGE_EXTENSIONS.includes(extension)) {
            setThumbnailFileError(`Unsupported file type. Allowed: ${ALLOWED_IMAGE_EXTENSIONS.join(", ")}`);
            setThumbnailFile(null);
            return;
        }

        setThumbnailFileError("");
        setThumbnailFile(selected);
        // Choosing a replacement file supersedes a pending removal.
        setRemoveThumbnail(false);
    };

    const handleRemoveThumbnail = () => {
        setThumbnailFile(null);
        setThumbnailFileError("");
        setRemoveThumbnail(true);
    };

    const handleUndoRemoveThumbnail = () => {
        setRemoveThumbnail(false);
    };

    // Same upload sequence as AddContent.js — MediaType.VIDEO regardless of
    // contentType, since the shared Media model has no AUDIO variant.
    const handleMediaUpload = async (selectedFile) => {
        const credentials = await initializeMediaUtil(selectedFile, false, "VIDEO");
        await uploadOnS3(selectedFile, credentials, setUploadProgress);
        return finalizeMediaUtil(credentials?.mediaId, false);
    };

    const handleTeacherGuideUpload = async (selectedFile) => {
        const credentials = await initializeMediaUtil(selectedFile, false, "VIDEO");
        await uploadOnS3(selectedFile, credentials, setTeacherGuideUploadProgress);
        return finalizeMediaUtil(credentials?.mediaId, false);
    };

    const handleThumbnailUpload = async (selectedFile) => {
        const credentials = await initializeMediaUtil(selectedFile, false, "IMAGE");
        await uploadOnS3(selectedFile, credentials, setThumbnailUploadProgress);
        return finalizeMediaUtil(credentials?.mediaId, false);
    };

    const handleSaveClick = async (values) => {
        try {
            setIsSaving(true);

            let mediaId;
            if (file) {
                const media = await handleMediaUpload(file);
                mediaId = media.id;
            }

            // undefined = omit from payload, leave existing teacher's-guide
            // video untouched; a number = replace it; null = explicitly clear it.
            let teacherGuideMediaId;
            if (teacherGuideFile) {
                const teacherGuideMedia = await handleTeacherGuideUpload(teacherGuideFile);
                teacherGuideMediaId = teacherGuideMedia.id;
            } else if (removeTeacherGuide) {
                teacherGuideMediaId = null;
            }

            // Same undefined/number/null convention as teacherGuideMediaId
            // above — undefined leaves whatever thumbnail already exists
            // (admin-set or auto-generated) untouched.
            let thumbnailMediaId;
            if (thumbnailFile) {
                const thumbnailMedia = await handleThumbnailUpload(thumbnailFile);
                thumbnailMediaId = thumbnailMedia.id;
            } else if (removeThumbnail) {
                thumbnailMediaId = null;
            }

            const isSlotEligibleState = SLOT_ELIGIBLE_STATE_KEYS.includes(values.stateKey);

            await updateAudioVideoContent({
                id,
                bodyData: {
                    title: values.title.trim(),
                    author: values.author?.trim() || undefined,
                    description: values.description?.trim() || undefined,
                    goal: values.goal?.trim() || undefined,
                    teacherInstructions: values.teacherInstructions?.trim() || undefined,
                    contentType: values.contentType,
                    stateKey: values.stateKey,
                    // Explicit null (not omitted) when leaving calm/focus —
                    // the backend also auto-clears this, but sending it
                    // makes the intent visible in the request rather than
                    // relying on that fallback silently doing the right thing.
                    activitySlot: isSlotEligibleState ? Number(values.activitySlot) : null,
                    ...(mediaId && { mediaId }),
                    ...(teacherGuideMediaId !== undefined && { teacherGuideMediaId }),
                    ...(thumbnailMediaId !== undefined && { thumbnailMediaId }),
                    contexts: values.contexts,
                },
            });

            setIsModalActive(true);
        } catch (error) {
            console.error("Error updating content:", error);
            toast.error("Failed to update content. Please try again.");
        } finally {
            setIsSaving(false);
            setUploadProgress(0);
            setTeacherGuideUploadProgress(0);
            setThumbnailUploadProgress(0);
        }
    };

    const handleModalYes = () => {
        setIsModalActive(false);
        history.push(APP_ROUTES.CONTENT_LIBRARY);
    };

    if (loading) {
        return <LoaderFallbackUI isLoader={loading} />;
    }

    if (loadError) {
        return (
            <div className="error-container">
                <HeadingWithBackArrow
                    backArrowClickHandler={backButtonHandler}
                    headingTitle={typography.EDIT_CONTENT}
                    showToggle={false}
                />
                <div className="alert alert-danger m-3">
                    <h5>Error</h5>
                    <p>{loadError}</p>
                </div>
            </div>
        );
    }

    // Status text for the preview panel's detailed (step 4) rows — the exact
    // same keep/replace/remove logic the upload widgets themselves use below,
    // just summarized as one line each instead of a preview + a link.
    const contentFileStatusLabel = file ? file.name : "Keeping existing file";
    const teacherGuideStatusLabel = teacherGuideFile
        ? teacherGuideFile.name
        : removeTeacherGuide
        ? "Will be removed"
        : existingContent?.teacherGuideVideoUrl
        ? "Keeping existing video"
        : "Not added";
    const thumbnailStatusLabel = thumbnailFile
        ? thumbnailFile.name
        : removeThumbnail
        ? "Will be removed"
        : existingContent?.thumbnailUrl
        ? "Keeping existing thumbnail"
        : "Auto-generated from video";

    return (
        <>
            <div className="wizard-page-heading">
                <HeadingWithBackArrow
                    backArrowClickHandler={backButtonHandler}
                    headingTitle={typography.EDIT_CONTENT}
                    showToggle={false}
                />
            </div>

            <div className="wizard-container">
                <WizardStepper
                    steps={WIZARD_STEPS}
                    currentStep={currentStep}
                    maxStepReached={maxStepReached}
                    onStepClick={goToStep}
                />

                <Form onSubmit={formik.handleSubmit}>
                    <div className="wizard-body">
                        <div className="wizard-card">
                            {currentStep === 0 && (
                                <>
                                    <div className="wizard-step-title">Basic Details</div>
                                    <div className="wizard-step-subtitle">
                                        Add the main information about your activity. Keep it short and clear.
                                    </div>

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label" for="content-title">
                                            {typography.TITLE}
                                            <span className="wizard-required-badge">*</span>
                                        </Label>
                                        <TextInput
                                            id="content-title"
                                            name="title"
                                            placeholder="e.g. Foot Press"
                                            value={formik.values.title}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            className="edit-notification-form-input hide-default-icon"
                                            required
                                        />
                                        <FormikErrorText formikInstance={formik} fieldName="title" />
                                    </FormGroup>

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label" for="content-author">
                                            {typography.AUTHOR}
                                            <span className="wizard-optional-badge">(optional)</span>
                                        </Label>
                                        <TextInput
                                            id="content-author"
                                            name="author"
                                            placeholder="e.g. Dr. Smith"
                                            value={formik.values.author}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            className="edit-notification-form-input hide-default-icon"
                                        />
                                        <FormikErrorText formikInstance={formik} fieldName="author" />
                                    </FormGroup>

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label" for="content-description">
                                            {typography.DESCRIPTION}
                                            <span className="wizard-optional-badge">(optional)</span>
                                        </Label>
                                        <textarea
                                            id="content-description"
                                            name="description"
                                            className="edit-notification-form-textarea"
                                            placeholder="Write a short description about this activity…"
                                            value={formik.values.description}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            rows={4}
                                        />
                                        <FormikErrorText formikInstance={formik} fieldName="description" />
                                    </FormGroup>
                                </>
                            )}

                            {currentStep === 1 && (
                                <>
                                    <div className="wizard-step-title">Category &amp; Type</div>
                                    <div className="wizard-step-subtitle">Choose where this content fits. This helps teachers find it easily.</div>

                                    <div className="wizard-field-row">
                                        <FormGroup className="wizard-field-group">
                                            <Label className="edit-notification-form-label" for="content-type">
                                                {typography.CONTENT_TYPE}
                                                <span className="wizard-required-badge">*</span>
                                            </Label>
                                            <select
                                                id="content-type"
                                                name="contentType"
                                                className="edit-notification-form-input"
                                                value={formik.values.contentType}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            >
                                                {CONTENT_TYPE_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <FormikErrorText formikInstance={formik} fieldName="contentType" />
                                        </FormGroup>

                                        <FormGroup className="wizard-field-group">
                                            <Label className="edit-notification-form-label" for="content-state">
                                                {typography.STATE}
                                                <span className="wizard-required-badge">*</span>
                                            </Label>
                                            <select
                                                id="content-state"
                                                name="stateKey"
                                                className="edit-notification-form-input"
                                                value={formik.values.stateKey}
                                                onChange={(e) => {
                                                    // Card 1 for calm/focus is permanent; a slot from
                                                    // the old state is meaningless once state no
                                                    // longer has slots at all, or just wrong for the
                                                    // other one — always start fresh, same as Add.
                                                    formik.setFieldValue("activitySlot", "");
                                                    formik.handleChange(e);
                                                }}
                                                onBlur={formik.handleBlur}
                                            >
                                                {STATE_KEY_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <FormikErrorText formikInstance={formik} fieldName="stateKey" />
                                        </FormGroup>
                                    </div>

                                    {isSlotEligible && (
                                        <FormGroup className="wizard-field-group">
                                            <Label className="edit-notification-form-label" for="content-activity-slot">
                                                {typography.ACTIVITY_SLOT}
                                                <span className="wizard-required-badge">*</span>
                                            </Label>
                                            <select
                                                id="content-activity-slot"
                                                name="activitySlot"
                                                className="edit-notification-form-input"
                                                value={formik.values.activitySlot}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            >
                                                <option value="" disabled>
                                                    Choose a card…
                                                </option>
                                                {ACTIVITY_SLOT_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <FormikErrorText formikInstance={formik} fieldName="activitySlot" />
                                        </FormGroup>
                                    )}

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label">Quick timing / context filters</Label>
                                        <div className="standard-typo-14-400 mb-2">
                                            Optional — select the situations where this activity is most useful.
                                        </div>
                                        <div className="wizard-checkbox-grid">
                                            {RESET_CONTEXT_OPTIONS.map((option) => (
                                                <label
                                                    key={option.value}
                                                    className="d-flex align-items-center gap-2 standard-typo-14-400"
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={formik.values.contexts.includes(option.value)}
                                                        onChange={() => handleContextToggle(option.value)}
                                                    />
                                                    {option.label}
                                                </label>
                                            ))}
                                        </div>
                                        <FormikErrorText formikInstance={formik} fieldName="contexts" />
                                    </FormGroup>
                                </>
                            )}

                            {currentStep === 2 && (
                                <>
                                    <div className="wizard-step-title">Additional Information</div>
                                    <div className="wizard-step-subtitle">
                                        Add helpful details for teachers. These will be shown on the activity preview screen.
                                    </div>

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label" for="content-goal">
                                            {typography.GOAL}
                                            <span className="wizard-optional-badge">(optional)</span>
                                        </Label>
                                        <div className="standard-typo-14-400 mb-2">{typography.GOAL_HELP}</div>
                                        <TextInput
                                            id="content-goal"
                                            name="goal"
                                            placeholder="e.g. Helps students relax and reduce anxiety"
                                            value={formik.values.goal}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            className="edit-notification-form-input hide-default-icon"
                                        />
                                        <FormikErrorText formikInstance={formik} fieldName="goal" />
                                    </FormGroup>

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label" for="content-teacher-instructions">
                                            {typography.TEACHER_INSTRUCTIONS}
                                            <span className="wizard-optional-badge">(optional)</span>
                                        </Label>
                                        <div className="standard-typo-14-400 mb-2">{typography.TEACHER_INSTRUCTIONS_HELP}</div>
                                        <textarea
                                            id="content-teacher-instructions"
                                            name="teacherInstructions"
                                            className="edit-notification-form-textarea"
                                            placeholder="Write brief instructions for teachers on how to run this activity…"
                                            value={formik.values.teacherInstructions}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            rows={4}
                                        />
                                        <FormikErrorText formikInstance={formik} fieldName="teacherInstructions" />
                                    </FormGroup>

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label" for="content-teacher-guide-file">
                                            {typography.TEACHER_GUIDE_VIDEO}
                                            <span className="wizard-optional-badge">(optional)</span>
                                        </Label>
                                        <div className="standard-typo-14-400 mb-2">{typography.TEACHER_GUIDE_VIDEO_HELP}</div>

                                        {existingContent?.teacherGuideVideoUrl && !removeTeacherGuide && !teacherGuideFile && (
                                            <div className="mb-2">
                                                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                                                <video controls src={existingContent.teacherGuideVideoUrl} className="content-thumb-preview" />
                                                <div>
                                                    <button type="button" className="btn btn-link p-0" onClick={handleRemoveTeacherGuide}>
                                                        {typography.REMOVE}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {removeTeacherGuide && (
                                            <div className="standard-typo-14-400 mb-2">
                                                Teacher preview video will be removed on save.{" "}
                                                <button type="button" className="btn btn-link p-0" onClick={handleUndoRemoveTeacherGuide}>
                                                    Undo
                                                </button>
                                            </div>
                                        )}

                                        {!existingContent?.teacherGuideVideoUrl && !teacherGuideFile && (
                                            <div className="standard-typo-14-400 mb-2">{typography.NOT_UPLOADED}</div>
                                        )}

                                        <FileDropzone
                                            id="content-teacher-guide-file"
                                            name="teacherGuideFile"
                                            accept={ALLOWED_MEDIA_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
                                            acceptedExtensionsLabel={ALLOWED_MEDIA_EXTENSIONS.map((ext) => ext.toUpperCase()).join(", ")}
                                            file={teacherGuideFile}
                                            error={teacherGuideFileError}
                                            uploadProgress={teacherGuideUploadProgress}
                                            isUploading={isSaving && !!teacherGuideFile}
                                            onChange={handleTeacherGuideFileChange}
                                            onRemove={() => {
                                                setTeacherGuideFile(null);
                                                setTeacherGuideFileError("");
                                            }}
                                        />
                                    </FormGroup>
                                </>
                            )}

                            {currentStep === 3 && (
                                <>
                                    <div className="wizard-step-title">Upload Files &amp; Review</div>
                                    <div className="wizard-step-subtitle">Add your content file and thumbnail. Review all details before saving.</div>

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label" for="content-file">
                                            {typography.FILE}
                                        </Label>
                                        <div className="standard-typo-14-400 mb-2">
                                            {file ? "Replacing with:" : "Current file kept — choose a new one only to replace it"}
                                        </div>
                                        <FileDropzone
                                            id="content-file"
                                            name="file"
                                            accept={ALLOWED_MEDIA_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
                                            acceptedExtensionsLabel={ALLOWED_MEDIA_EXTENSIONS.map((ext) => ext.toUpperCase()).join(", ")}
                                            file={file}
                                            error={fileError}
                                            uploadProgress={uploadProgress}
                                            isUploading={isSaving && !!file}
                                            onChange={handleFileChange}
                                            onRemove={() => {
                                                setFile(null);
                                                setFileError("");
                                            }}
                                        />
                                    </FormGroup>

                                    <FormGroup className="wizard-field-group">
                                        <Label className="edit-notification-form-label" for="content-thumbnail-file">
                                            {typography.THUMBNAIL}
                                            <span className="wizard-optional-badge">(optional)</span>
                                        </Label>
                                        <div className="standard-typo-14-400 mb-2">{typography.THUMBNAIL_HELP}</div>

                                        {existingContent?.thumbnailUrl && !removeThumbnail && !thumbnailFile && (
                                            <div className="mb-2">
                                                {/* eslint-disable-next-line jsx-a11y/alt-text */}
                                                <img src={existingContent.thumbnailUrl} alt="" className="content-thumb-preview" />
                                                <div>
                                                    <button type="button" className="btn btn-link p-0" onClick={handleRemoveThumbnail}>
                                                        {typography.REMOVE}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {removeThumbnail && (
                                            <div className="standard-typo-14-400 mb-2">
                                                Thumbnail will be removed on save.{" "}
                                                <button type="button" className="btn btn-link p-0" onClick={handleUndoRemoveThumbnail}>
                                                    Undo
                                                </button>
                                            </div>
                                        )}

                                        {!existingContent?.thumbnailUrl && !thumbnailFile && (
                                            <div className="standard-typo-14-400 mb-2">{typography.NOT_UPLOADED}</div>
                                        )}

                                        <FileDropzone
                                            id="content-thumbnail-file"
                                            name="thumbnailFile"
                                            accept={ALLOWED_IMAGE_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
                                            acceptedExtensionsLabel={ALLOWED_IMAGE_EXTENSIONS.map((ext) => ext.toUpperCase()).join(", ")}
                                            file={thumbnailFile}
                                            error={thumbnailFileError}
                                            uploadProgress={thumbnailUploadProgress}
                                            isUploading={isSaving && !!thumbnailFile}
                                            onChange={handleThumbnailFileChange}
                                            onRemove={() => {
                                                setThumbnailFile(null);
                                                setThumbnailFileError("");
                                            }}
                                        />
                                    </FormGroup>
                                </>
                            )}

                            <div className="wizard-footer">
                                <CustomButton
                                    title="← Back"
                                    type="button"
                                    color="light"
                                    onClick={goBack}
                                    disabled={currentStep === 0}
                                />
                                {currentStep < WIZARD_STEPS.length - 1 ? (
                                    <CustomButton
                                        key="wizard-next-btn"
                                        title="Next →"
                                        type="button"
                                        color="info"
                                        onClick={goNext}
                                        disabled={!isStepValid(currentStep)}
                                    />
                                ) : (
                                    <CustomButton
                                        key="wizard-save-btn"
                                        title={isSaving ? "Saving…" : "Save"}
                                        className="edit-notification-update-button"
                                        onClick={formik.handleSubmit}
                                        type="submit"
                                        loading={isSaving}
                                    />
                                )}
                            </div>
                        </div>

                        <PreviewCard
                            values={formik.values}
                            thumbnailPreviewUrl={thumbnailPreviewUrl || existingContent?.thumbnailUrl}
                            contentFileStatusLabel={contentFileStatusLabel}
                            teacherGuideStatusLabel={teacherGuideStatusLabel}
                            thumbnailStatusLabel={thumbnailStatusLabel}
                            detailed={currentStep === 3}
                        />
                    </div>
                </Form>
            </div>

            <ConfirmationModal
                isOpen={isModalActive}
                modalType="CONTENT_UPDATED"
                onYes={handleModalYes}
                size="md"
                showNoButton={false}
                customImageSrc={ModalSuccessImage}
            />
        </>
    );
}
